import { useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import './scaleBar.css';

const DEFAULT_STEP = 1;

interface ScaleBarCommonProps {
  min: number;
  max: number;
  step?: number;
  ticks?: readonly string[];
  formatValue?: (value: number) => string;
  showValues?: boolean;
  className?: string;
}

interface ScaleBarSingleProps extends ScaleBarCommonProps {
  mode?: 'single';
  value: number;
  ariaLabel: string;
  onChange: (value: number) => void;
}

interface ScaleBarRangeProps extends ScaleBarCommonProps {
  mode: 'range';
  value: readonly [number, number];
  minAriaLabel: string;
  maxAriaLabel: string;
  onChange: (value: [number, number]) => void;
}

export type ScaleBarProps = ScaleBarSingleProps | ScaleBarRangeProps;

function clamp(value: number, lower: number, upper: number): number {
  return Math.min(Math.max(value, lower), upper);
}

export function ScaleBar(props: ScaleBarProps) {
  const { min, max, step = DEFAULT_STEP, ticks, formatValue, showValues = true, className = '' } = props;
  const controlRef = useRef<HTMLDivElement>(null);
  const [nearestThumb, setNearestThumb] = useState<'min' | 'max'>('min');

  const span = max - min || 1;
  const ratio = (value: number) => clamp((value - min) / span, 0, 1);
  const label = (value: number) => (formatValue ? formatValue(value) : String(value));

  // The thumb centre travels over (track width - thumb width), so a plain
  // percentage drifts by half a thumb at both ends.
  const thumbOffset = (value: number) => {
    const shift = 0.5 - ratio(value);
    return `calc(${(ratio(value) * 100).toFixed(4)}% ${shift < 0 ? '-' : '+'} ${Math.abs(shift).toFixed(4)} * var(--scale-thumb-size))`;
  };
  const trailingOffset = (value: number) => {
    const shift = 0.5 - ratio(value);
    return `calc(${((1 - ratio(value)) * 100).toFixed(4)}% ${shift < 0 ? '+' : '-'} ${Math.abs(shift).toFixed(4)} * var(--scale-thumb-size))`;
  };

  const isRange = props.mode === 'range';
  const low = isRange ? props.value[0] : min;
  const high = isRange ? props.value[1] : props.value;

  // Overlapping thumbs would otherwise both hand every drag to the topmost
  // input; hover decides which one is reachable before the pointer goes down.
  const trackNearestThumb = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!isRange || !controlRef.current) return;
    const bounds = controlRef.current.getBoundingClientRect();
    if (bounds.width === 0) return;
    const pointerRatio = clamp((event.clientX - bounds.left) / bounds.width, 0, 1);
    setNearestThumb(Math.abs(pointerRatio - ratio(low)) <= Math.abs(pointerRatio - ratio(high)) ? 'min' : 'max');
  };

  const changeLow = (next: number) => {
    if (!isRange) return;
    props.onChange([Math.min(next, high - step), high]);
  };
  const changeHigh = (next: number) => {
    if (isRange) {
      props.onChange([low, Math.max(next, low + step)]);
      return;
    }
    props.onChange(next);
  };

  return (
    <div
      className={`scale-bar${showValues ? ' scale-bar--with-values' : ''} ${className}`.trim()}
      onPointerMove={trackNearestThumb}
    >
      <div className="scale-bar__control" ref={controlRef}>
        <span className="scale-bar__track" aria-hidden />
        <span
          className="scale-bar__fill"
          style={{ left: isRange ? thumbOffset(low) : 0, right: trailingOffset(high) }}
          aria-hidden
        />
        {isRange && (
          <>
            {showValues && <span className="scale-bar__bubble" style={{ left: thumbOffset(low) }} aria-hidden>{label(low)}</span>}
            <input
              className="scale-bar__input"
              style={{ zIndex: nearestThumb === 'min' ? 2 : 1 }}
              type="range"
              aria-label={props.minAriaLabel}
              min={min}
              max={max}
              step={step}
              value={low}
              onChange={event => changeLow(Number(event.target.value))}
            />
          </>
        )}
        {showValues && <span className="scale-bar__bubble" style={{ left: thumbOffset(high) }} aria-hidden>{label(high)}</span>}
        <input
          className="scale-bar__input"
          style={{ zIndex: !isRange || nearestThumb === 'max' ? 2 : 1 }}
          type="range"
          aria-label={isRange ? props.maxAriaLabel : props.ariaLabel}
          min={min}
          max={max}
          step={step}
          value={high}
          onChange={event => changeHigh(Number(event.target.value))}
        />
      </div>
      {ticks && ticks.length > 0 && (
        <div className="scale-bar__ticks" aria-hidden>
          {ticks.map(tick => <span key={tick}>{tick}</span>)}
        </div>
      )}
    </div>
  );
}
